
import { Category } from '@/types';
import { apiService } from './apiService';
import { API_CONFIG } from '@/config/apiConfig';
import { generateId, storeCategories } from './storageService';
import * as indexedDBService from './indexedDBService';

// Fetch all categories from API
export const fetchCategories = async (): Promise<Category[]> => {
  try {
    if (!API_CONFIG.baseURL) {
      console.log('No API URL configured, returning sample categories');
      // Return mock categories for development
      return getSampleCategories();
    }

    console.log('Fetching categories from API:', `${API_CONFIG.baseURL}${API_CONFIG.endpoints.categories.list}`);
    const url = API_CONFIG.endpoints.categories.list;
    const response = await apiService.get<any[]>(url);
    
    console.log(`Fetched ${response?.length || 0} categories from API`);
    
    if (!response || !Array.isArray(response)) {
      console.warn('Invalid response format for categories:', response);
      return getSampleCategories();
    }
    
    // Map the API response to our Category type
    const mappedCategories = response.map(item => {
      // Debug logging to see the actual structure of the first item
      if (response.indexOf(item) === 0) {
        console.log('Sample category from API:', item);
      }
      
      // Check if API is returning the expected field structure
      const hasUpperCaseFields = item.ID !== undefined && item.Descricao !== undefined;
      
      return {
        id: hasUpperCaseFields ? String(item.ID) : (item.id || generateId()),
        name: hasUpperCaseFields ? item.Descricao : (item.name || 'Unknown Category'),
        image: hasUpperCaseFields ? (item.Imagem || '/icons/fallback-icon.svg') : (item.image || '/icons/fallback-icon.svg')
      };
    });
    
    console.log(`Mapped ${mappedCategories.length} categories to correct format`);
    return mappedCategories;
  } catch (error) {
    console.error('Error fetching categories:', error);
    return getSampleCategories();
  }
};

// Fetch a specific category by ID
export const fetchCategoryById = async (id: string): Promise<Category | null> => {
  try {
    if (!API_CONFIG.baseURL) {
      const sampleCategories = getSampleCategories();
      return sampleCategories.find(c => c.id === id) || null;
    }

    const url = API_CONFIG.endpoints.categories.byId.replace(':id', id);
    const response = await apiService.get<any>(url);
    
    // Map the API response to our Category type
    if (response) {
      const hasUpperCaseFields = response.ID !== undefined && response.Descricao !== undefined;
      
      return {
        id: hasUpperCaseFields ? String(response.ID) : (response.id || id),
        name: hasUpperCaseFields ? response.Descricao : (response.name || 'Unknown Category'),
        image: hasUpperCaseFields ? (response.Imagem || '/icons/fallback-icon.svg') : (response.image || '/icons/fallback-icon.svg')
      };
    }
    
    return null;
  } catch (error) {
    console.error(`Error fetching category with ID ${id}:`, error);
    return null;
  }
};

// Sync categories with API
export const syncCategoriesWithApi = async (): Promise<boolean> => {
  try {
    console.log('Starting category synchronization process...');
    const categories = await fetchCategories();
    
    if (categories && categories.length > 0) {
      console.log(`Successfully fetched ${categories.length} categories, storing them...`);
      
      // Clear existing categories before storing new ones to avoid duplication
      try {
        await indexedDBService.clearStore('categories');
        console.log('Cleared existing categories before syncing');
        
        await storeCategories(categories);
        console.log(`Successfully stored ${categories.length} categories`);
        return true;
      } catch (storageError) {
        console.error('Error saving categories:', storageError);
        return false;
      }
    }
    console.log('No categories fetched or empty categories array');
    return false;
  } catch (error) {
    console.error('Error syncing categories with API:', error);
    return false;
  }
};

// Sample categories for development/testing
const getSampleCategories = (): Category[] => {
  return [
    { id: generateId(), name: 'Bebidas', image: '/icons/drinks.png' },
    { id: generateId(), name: 'Entradas', image: '/icons/appetizers.png' },
    { id: generateId(), name: 'Pratos Principais', image: '/icons/main_courses.png' },
    { id: generateId(), name: 'Sobremesas', image: '/icons/desserts.png' },
    { id: generateId(), name: 'Lanches', image: '/icons/snacks.png' }
  ];
};
